import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { CURRENCY } from 'App';
import { cancelTicket } from 'services/api/slipService';
import { Slip } from 'types/Slip';
import styles from './StornoButton.module.css';

interface Props {
  slip: Slip;
  /** Called after the ticket was cancelled (reload the tickets). */
  onCancelled: () => void;
  compact?: boolean;
}

const clock = (ms: number) => {
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Storno of a prematch ticket: the first five minutes after placing it (cut
 * to the first kick-off) the player may cancel it and gets the stake back.
 * The server says how long is left; the button counts it down and goes.
 */
export const StornoButton = ({ slip, onCancelled, compact }: Props) => {
  const { t } = useTranslation();
  const leftMs = Number(slip.storno_left_ms ?? 0);
  // Counted from when this copy of the ticket arrived, not the phone's clock.
  const deadline = useMemo(() => Date.now() + leftMs, [slip, leftMs]); // eslint-disable-line react-hooks/exhaustive-deps
  const [left, setLeft] = useState(leftMs);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLeft(deadline - Date.now());
    if (deadline <= Date.now()) return;
    const id = window.setInterval(() => {
      const l = deadline - Date.now();
      setLeft(l);
      if (l <= 0) window.clearInterval(id);
    }, 500);
    return () => window.clearInterval(id);
  }, [deadline]);

  if (String(slip.status).toLowerCase() !== 'pending' || left <= 0) {
    return null;
  }

  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const stake = `${Number(slip.stake).toFixed(2)} ${CURRENCY}`;

  const cancel = async () => {
    setBusy(true);
    const res = await cancelTicket(slip.id);
    setBusy(false);
    setConfirm(false);
    if (res.ok) {
      toast.success(t('storno_success_label', { amount: stake }));
      onCancelled();
    } else {
      toast.error(t('storno_closed_label'));
      setLeft(0);
    }
  };

  if (confirm) {
    return (
      <div
        className={classNames(styles.box, compact && styles.compact)}
        onClick={stop}
      >
        <span>{t('storno_confirm_label', { amount: stake })}</span>
        <button
          type="button"
          className={styles.yes}
          disabled={busy}
          onClick={cancel}
        >
          {t('yes_label', { defaultValue: 'Yes' })}
        </button>
        <button
          type="button"
          className={styles.no}
          disabled={busy}
          onClick={() => setConfirm(false)}
        >
          {t('no_label', { defaultValue: 'No' })}
        </button>
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.box, compact && styles.compact)}
      onClick={stop}
    >
      <button
        type="button"
        className={styles.storno}
        onClick={() => setConfirm(true)}
        title={t('storno_hint_label')}
      >
        {t('storno_label')}
        <strong>{clock(left)}</strong>
      </button>
    </div>
  );
};
